import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Card from '@wtag/rcl-card';
import ContentLoaderPlaceholder from '@wtag/rcl-content-loader-placeholder';
import { FormatDateTime, DATE_FORMAT_SHORT_WITH_TIME } from 'sharedWebpack/helpers/dateTime';
import DateTimeLocaleInterpolationWrapper from 'sharedWebpack/DateTimeLocaleInterpolationWrapper';
import AirportChangeSegment from 'sharedWebpack/AirportChangeSegment';
import './styles.scss';

const getAirportData = async (findAirport, code) => {
  const result = await findAirport(code);
  return result?.type === 'AIRPORT_DETAILS_FETCHED' ? result.data : result;
};

const viewAirportDetails = airport => {
  const { name, placeName, countryName } = airport;
  const values = [name, placeName, countryName].filter(value => !!value);
  return values.join(', ');
};

const StopoverDetails = ({ findAirport, stopover, airportChangeMap }) => {
  const { airportCode, arrivalLocaltime, arrivalLocaltimeInUserTz } = stopover;
  const stopoverArrivalLocaltime = arrivalLocaltime || arrivalLocaltimeInUserTz;

  const [stopoverAirport, setStopoverAirport] = useState(null);

  const [arrivalAirport, setArrivalAirport] = useState(null);
  const [departureAirport, setDepartureAirport] = useState(null);

  const fetchAirports = async () => {
    const fetchedStopoverAirport = await getAirportData(findAirport, airportCode);

    setStopoverAirport(fetchedStopoverAirport);

    const airportChangeData = airportChangeMap.get(fetchedStopoverAirport?.code);
    if (!airportChangeData) return;

    const fetchedArrivalAirport = await getAirportData(findAirport, airportChangeData.arrival);

    setArrivalAirport(fetchedArrivalAirport);

    if (airportChangeData.departure === fetchedStopoverAirport.code) {
      setDepartureAirport(fetchedStopoverAirport);
    } else {
      const fetchedDepartureAirport = await getAirportData(
        findAirport,
        airportChangeData.departure,
      );

      setDepartureAirport(fetchedDepartureAirport);
    }
  };

  useEffect(() => {
    setStopoverAirport(null);
    setArrivalAirport(null);
    setDepartureAirport(null);

    fetchAirports();
  }, [airportCode, findAirport, airportChangeMap]);

  const hasAirportChange = airportChangeMap.has(stopoverAirport?.code);

  return (
    <Card className="duration-stopover__card" key={airportCode} version="v2">
      {stopoverAirport ? (
        <div className="stopover-details__content">
          <div className="col-grid col-bleed direction-row align-center justify-space-between stopover-details">
            <div className="stopover-details__country">
              <img
                className="stopover-details__flag"
                src={stopoverAirport.countryFlag}
                alt={stopoverAirport.countryName}
              />
              {airportCode}
            </div>
          </div>

          {stopoverAirport.name && (
            <div className="stopover-details__airport-name">
              {viewAirportDetails(stopoverAirport)}
            </div>
          )}

          <div className="stopover-details__arrival-time">
            <DateTimeLocaleInterpolationWrapper
              localeKey="components.flight.stopover.arrival"
              time={FormatDateTime({
                dateTime: stopoverArrivalLocaltime,
                format: DATE_FORMAT_SHORT_WITH_TIME,
                displayWithoutConversion: true,
              })}
            />
          </div>

          {hasAirportChange &&
            (arrivalAirport && departureAirport ? (
              <AirportChangeSegment
                destinationAirportDetails={arrivalAirport}
                changedAirportDetails={departureAirport}
              />
            ) : (
              <ContentLoaderPlaceholder numberOfLines={2} />
            ))}
        </div>
      ) : (
        <span className="stopover-details__content-placeholder">
          <ContentLoaderPlaceholder numberOfLines={3} />
        </span>
      )}
    </Card>
  );
};

StopoverDetails.propTypes = {
  findAirport: PropTypes.func.isRequired,
  stopover: PropTypes.shape({
    airportCode: PropTypes.string.isRequired,
    arrivalLocaltime: PropTypes.string,
    arrivalLocaltimeInUserTz: PropTypes.string,
  }).isRequired,
  airportChangeMap: PropTypes.instanceOf(Map).isRequired,
};

export default StopoverDetails;
